// Iteration 138 -- Match Availability respond surface (parent + player).
// Lists pending (no_reply) availability requests for the signed-in user's own
// player record and/or linked children, with one-tap Available / Out / Maybe.
// Used on My Children (parent) and My Home (player). Self-hides when empty.

import { useEffect, useState } from "react";
import { CalendarCheck, Check, X, HelpCircle } from "lucide-react";
import type {
  PendingAvailabilityRequest,
  MatchAvailabilityReply,
} from "@dugads/shared";
import * as api from "@/lib/api";

const formatKickoff = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const REPLY_BUTTONS: Array<{ status: MatchAvailabilityReply; label: string; Icon: typeof Check; active: string }> = [
  { status: "available", label: "Available", Icon: Check, active: "bg-[#085041] text-white" },
  { status: "maybe", label: "Maybe", Icon: HelpCircle, active: "bg-[#633806] text-white" },
  { status: "out", label: "Out", Icon: X, active: "bg-[#791F1F] text-white" },
];

/** `showHeading` lets the host page decide whether to render its own section title. */
export function AvailabilityRequestsCard({ showHeading = true }: { showHeading?: boolean }) {
  const [requests, setRequests] = useState<PendingAvailabilityRequest[] | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getMyAvailabilityRequests()
      .then((res) => {
        if (!cancelled) setRequests(res.requests);
      })
      .catch(() => {
        if (!cancelled) setRequests([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hidden entirely until loaded, and when there is nothing to reply to.
  if (requests === null || requests.length === 0) return null;

  async function reply(req: PendingAvailabilityRequest, status: MatchAvailabilityReply) {
    const key = `${req.match_id}:${req.player_id}`;
    setBusyKey(key);
    try {
      await api.respondMatchAvailability(req.match_id, { player_id: req.player_id, status });
      // Once answered it is no longer pending -- drop it from the list.
      setRequests((prev) => (prev ? prev.filter((r) => !(r.match_id === req.match_id && r.player_id === req.player_id)) : prev));
    } catch {
      /* leave the row so the user can retry */
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-border p-4"
      data-region="availability-requests"
    >
      {showHeading && (
        <h2 className="flex items-center gap-2 text-lg font-medium">
          <CalendarCheck className="h-5 w-5 text-primary" aria-hidden /> Availability requests ({requests.length})
        </h2>
      )}
      <ul className="flex flex-col gap-2">
        {requests.map((req) => {
          const key = `${req.match_id}:${req.player_id}`;
          return (
            <li
              key={key}
              className="flex flex-col gap-2 rounded-md border border-border bg-card px-3 py-2.5"
              data-availability-request={key}
            >
              <div>
                <p className="text-sm font-medium text-foreground">
                  {req.player_name} &middot; {req.team_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {req.event_name}
                  {req.opponents.length > 0 ? ` vs ${req.opponents.join(", ")}` : ""} &middot; {formatKickoff(req.starts_at)}
                  {req.venue ? ` · ${req.venue}` : ""}
                </p>
              </div>
              {/* Full-width In / Maybe / Out segmented on mobile; inline on desktop. */}
              <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
                {REPLY_BUTTONS.map(({ status, label, Icon, active }) => {
                  const isActive = req.current_status === status;
                  return (
                    <button
                      key={status}
                      type="button"
                      data-action={`respond-${status}`}
                      disabled={busyKey === key}
                      onClick={() => reply(req, status)}
                      className={`inline-flex items-center justify-center gap-1 rounded-md border px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
                        isActive ? active : "border-input text-foreground hover:bg-muted"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
                    </button>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
